import { Box, Checkbox, FormControl } from "@primer/react";
import { DateBox } from "devextreme-react/date-box";
import { SelectBox } from "devextreme-react/select-box";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { hinhThucMienGiamApi } from "../../api/hinhThucMienGiamApi";
import TagboxHinhThucNopBom from "../../components/tagbox-hinh-thuc-nop-bom";
import Button from "../../components/ui/button";
import Modal from "../../components/ui/modal";
import ModalActions from "../../components/ui/modal/ModalActions";
import Text from "../../components/ui/text";
import TextInput from "../../components/ui/text-input";
import FileViewAndUpload from "../../components/file-view-and-upload";
import SelectBoxNhomMienGiam from "../../components/selectbox-nhommiengiam";
import CmbHinhThucNopPhi from "../../components/combobox-hinh-thuc-nop-phi/CmbHinhThucNopPhi";
import { showNotify } from "../../helpers/toast";
import { IAttachment } from "../../models/response/upload/IAttachment";
import { sf_hinhthucmiengiam } from "../../models/response/category/sf_hinhthucmiengiam";
import { loadHinhThucMienGiamHinhThucTinhStartAction } from "../../state/actions/hinhThucMienGiamActions";
import { RootState } from "../../state/reducers";
import Steps, { IStepData } from "../../components/steps/Steps";
import {
  MienGiamItemsModal,
  MienGiamItemsSubmitHandler,
  MienGiamItemsSubmitPayload,
} from "./detail_items";

type HinhThucMienGiamWizardModalProps = {
  sf_hinhthucmiengiam?: sf_hinhthucmiengiam;
  initialStep?: 1 | 2;
  nam_hoc?: string;
  onClose: (shouldReload?: boolean) => void;
  onChanged: () => void;
};

type WizardStep = 1 | 2;

const createDataObject = (
  entries: [number, number][]
): Record<number, number> => {
  return Object.fromEntries(entries);
};

const dataObject = createDataObject([
  [9, 1],
  [10, 2],
  [11, 3],
  [12, 4],
  [13, 5],
  [14, 6],
  [15, 7],
  [16, 8],
  [17, 9],
  [18, 10],
  [7, 11],
  [8, 12],
  [2, 13],
  [3, 14],
  [1, 15],
]);

const createDefaultFormValues = (
  sf_hinhthucmiengiam: sf_hinhthucmiengiam | undefined,
  nam_hoc: string,
  dm_coso_id: number
): sf_hinhthucmiengiam => {
  return (
    sf_hinhthucmiengiam ?? {
      id: 0,
      hinh_thuc_mien_giam: "",
      is_active: true,
      ma_hinh_thuc: "",
      nam_hoc,
      nhom_mien_giam: 1,
      sf_hinhthucmiengiam_hinhthuctinh_id: 0,
      mien_giam_from_nhom: 1,
      so_lan_huong_toi_da: 1,
      dm_coso_id,
      sf_hinhthucnopbom_ids: "1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18",
      DVT: "",
      tu_ngay: moment().format("YYYY-MM-DD"),
      den_ngay: moment().format("YYYY-MM-DD"),
      is_apdung_toantruong: false,
      sf_hinhthucmiengiam_group_id: 0,
      attach_files: "",
      sf_saleset_register_type_id: 0,
    }
  );
};

const HinhThucMienGiamWizardModal = (
  props: HinhThucMienGiamWizardModalProps
) => {
  const dispatch = useDispatch();
  const { dm_coso_id } = useSelector((x: RootState) => x.common);
  const sf_hinhthucmiengiam_hinhthuctinhs = useSelector(
    (x: RootState) => x.hinhThucMienGiam.sf_hinhthucmiengiam_hinhthuctinhs
  );

  const defaultFormValues = useMemo(
    () =>
      createDefaultFormValues(
        props.sf_hinhthucmiengiam,
        props.nam_hoc || "",
        dm_coso_id
      ),
    [props.sf_hinhthucmiengiam, props.nam_hoc, dm_coso_id]
  );

  const [currentStep, setCurrentStep] = useState<WizardStep>(
    props.initialStep === 2 && defaultFormValues.id > 0 ? 2 : 1
  );
  const [currentRecord, setCurrentRecord] =
    useState<sf_hinhthucmiengiam>(defaultFormValues);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingStep2, setIsSavingStep2] = useState(false);
  const [submitStep2Changes, setSubmitStep2Changes] =
    useState<MienGiamItemsSubmitHandler | null>(null);
  const [hasChanged, setHasChanged] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<sf_hinhthucmiengiam>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (sf_hinhthucmiengiam_hinhthuctinhs.length === 0) {
      dispatch(loadHinhThucMienGiamHinhThucTinhStartAction());
    }
  }, [dispatch, sf_hinhthucmiengiam_hinhthuctinhs.length]);

  useEffect(() => {
    setCurrentRecord(defaultFormValues);
    reset(defaultFormValues);
    setCurrentStep(props.initialStep === 2 && defaultFormValues.id > 0 ? 2 : 1);
  }, [defaultFormValues, props.initialStep, reset]);

  const saveThongTinChung = async (
    data: sf_hinhthucmiengiam,
    nextStep?: WizardStep
  ) => {
    setIsSaving(true);
    try {
      const payload = {
        ...data,
        dm_coso_id,
      };
      const res = payload.id > 0
        ? await hinhThucMienGiamApi.update(payload)
        : await hinhThucMienGiamApi.insert(payload);

      if (!res.is_success) {
        showNotify({
          message: res.message || "Lưu hình thức miễn giảm thất bại",
          type: "error",
        });
        return;
      }

      const savedId = payload.id > 0 ? payload.id : Number(res.data || 0);
      const savedRecord = {
        ...payload,
        id: savedId,
      };

      setCurrentRecord(savedRecord);
      reset(savedRecord);
      setHasChanged(true);
      props.onChanged();
      showNotify({
        message: `${payload.id > 0 ? "Cập nhật" : "Thêm mới"} hình thức miễn giảm thành công`,
        type: "success",
      });

      if (nextStep) {
        setCurrentStep(nextStep);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    props.onClose(hasChanged);
  };

  const handleFinishStep2 = async () => {
    if (!submitStep2Changes) {
      props.onClose(hasChanged);
      return;
    }

    const payload: MienGiamItemsSubmitPayload[] = submitStep2Changes();
    if (payload.length === 0) {
      props.onClose(hasChanged);
      return;
    }

    setIsSavingStep2(true);
    try {
      const res = await hinhThucMienGiamApi.save_change_chitiets(payload);
      if (!res.is_success) {
        showNotify({
          message: "Có lỗi " + (res.message || ""),
          type: "error",
        });
        return;
      }

      showNotify({
        message: "Cập nhật thành công",
        type: "success",
      });
      props.onChanged();
      props.onClose(true);
    } finally {
      setIsSavingStep2(false);
    }
  };

  const canGoToStep2 = currentRecord.id > 0;
  const steps: IStepData[] = useMemo(
    () => [
      { id: 1, name: "Thông tin chung", is_active: currentStep === 1 },
      {
        id: 2,
        name: "Điều chỉnh khoản miễn giảm",
        is_active: currentStep === 2,
      },
    ],
    [currentStep]
  );

  return (
    <Modal
      isOpen
      title={currentRecord.id > 0 ? "Cập nhật hình thức miễn giảm" : "Thêm mới hình thức miễn giảm"}
      onClose={handleClose}
      width="80%"
    >
      <Box sx={{ px: 3, pt: 0, pb: 0, mt: -2 }}>
        <Steps
          steps={steps}
          onChangedStep={(step) => {
            if (step === 1) {
              setCurrentStep(1);
            }
            if (step === 2 && canGoToStep2) {
              setCurrentStep(2);
            }
          }}
        />
        {!canGoToStep2 && (
          <Box sx={{ color: "fg.muted", fontSize: 1, mt: 2 }}>
            Cần lưu bước 1 trước để mở bước 2 điều chỉnh khoản miễn giảm.
          </Box>
        )}
      </Box>

      {currentStep === 1 && (
        <form
          onSubmit={handleSubmit((data) => saveThongTinChung(data, 2))}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              px: 3,
              py: 3,
            }}
          >
            <FormControl>
              <FormControl.Label>Nhóm miễn giảm</FormControl.Label>
              <Controller
                control={control}
                name="sf_hinhthucmiengiam_group_id"
                rules={{
                  required: {
                    value: true,
                    message: "Vui lòng chọn nhóm miễn giảm",
                  },
                }}
                render={({ field }) => (
                  <SelectBoxNhomMienGiam
                    value={field.value}
                    isShowClearBtn={true}
                    onValueChanged={(value) => {
                      field.onChange(value);
                    }}
                    width={"100%"}
                  />
                )}
              />
              {errors?.["sf_hinhthucmiengiam_group_id"] && (
                <FormControl.Validation variant="error">
                  Vui lòng chọn nhóm miễn giảm
                </FormControl.Validation>
              )}
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <Text text="Mã hình thức" />
              </FormControl.Label>
              <TextInput
                block
                name="ma_hinh_thuc"
                register={register}
                errors={errors}
                required
                validateMessage="Vui lòng điền mã hình thức miễn giảm"
              />
            </FormControl>

            <FormControl sx={{ gridColumn: "1 / span 2" }}>
              <FormControl.Label>
                <Text text="Nội dung miễn giảm" />
              </FormControl.Label>
              <TextInput
                block
                name="hinh_thuc_mien_giam"
                register={register}
                errors={errors}
                required
                validateMessage="Vui lòng điền nội dung miễn giảm"
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>Hình thức tính</FormControl.Label>
              <Controller
                control={control}
                name="sf_hinhthucmiengiam_hinhthuctinh_id"
                rules={{
                  required: {
                    value: true,
                    message: "Vui lòng chọn hình thức tính miễn giảm",
                  },
                }}
                render={({ field }) => (
                  <SelectBox
                    dataSource={sf_hinhthucmiengiam_hinhthuctinhs}
                    displayExpr="hinh_thuc_tinh"
                    valueExpr="id"
                    value={field.value}
                    onValueChanged={(e) => field.onChange(e.value)}
                    width="100%"
                    height={32}
                    stylingMode="outlined"
                  />
                )}
              />
              {errors?.["sf_hinhthucmiengiam_hinhthuctinh_id"] && (
                <FormControl.Validation variant="error">
                  Vui lòng chọn hình thức tính miễn giảm
                </FormControl.Validation>
              )}
            </FormControl>

            <FormControl>
              <FormControl.Label>
                <Text text="Đơn vị tính" />
              </FormControl.Label>
              <TextInput
                block
                name="DVT"
                register={register}
                errors={errors}
                required={false}
                validateMessage="Vui lòng điền đơn vị tính"
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>Áp dụng cho kỳ nộp</FormControl.Label>
              <Controller
                control={control}
                name="sf_hinhthucnopbom_ids"
                render={({ field }) => (
                  <TagboxHinhThucNopBom
                    width={"100%"}
                    value={
                      field.value
                        ? field.value.split(",").map((x) => parseInt(x) || 0)
                        : []
                    }
                    onValueChanged={(values: number[]) => {
                      const sortedValues = values.sort((a, b) => {
                        return (
                          (dataObject[a] || Infinity) -
                          (dataObject[b] || Infinity)
                        );
                      });
                      field.onChange(sortedValues.join(","));
                    }}
                    dmCoSoId={dm_coso_id}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>Áp dụng cho hình thức nộp</FormControl.Label>
              <Controller
                control={control}
                name="sf_saleset_register_type_id"
                render={({ field }) => (
                  <CmbHinhThucNopPhi
                    width={"100%"}
                    value={field.value as number}
                    onValueChanged={(id: number) => {
                      field.onChange(id);
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>Từ ngày</FormControl.Label>
              <Controller
                control={control}
                name="tu_ngay"
                render={({ field }) => (
                  <DateBox
                    width={"100%"}
                    className={"margin-right-10"}
                    value={field.value}
                    onValueChange={(value) => {
                      if (value !== null) {
                        field.onChange(moment(value).format("YYYY-MM-DD"));
                      } else {
                        field.onChange("");
                      }
                    }}
                    displayFormat={"dd-MM-yyyy"}
                    dateSerializationFormat={"yyyy-MM-ddTHH:mm:ss"}
                    readOnly={false}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>Đến ngày</FormControl.Label>
              <Controller
                control={control}
                name="den_ngay"
                render={({ field }) => (
                  <DateBox
                    width={"100%"}
                    className={"margin-right-10"}
                    value={field.value}
                    onValueChange={(value) => {
                      if (value !== null) {
                        field.onChange(moment(value).format("YYYY-MM-DD"));
                      } else {
                        field.onChange("");
                      }
                    }}
                    displayFormat={"dd-MM-yyyy"}
                    dateSerializationFormat={"yyyy-MM-ddTHH:mm:ss"}
                    readOnly={false}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>Áp dụng</FormControl.Label>
              <Controller
                control={control}
                name="is_active"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>Áp dụng cho toàn trường</FormControl.Label>
              <Controller
                control={control}
                name="is_apdung_toantruong"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl sx={{ gridColumn: "1 / span 2" }}>
              <FormControl.Label>Đính kèm file</FormControl.Label>
              <Controller
                control={control}
                name="attach_files"
                render={({ field }) => (
                  <FileViewAndUpload
                    accept="*"
                    files={(() => {
                      try {
                        const parsedFiles: IAttachment[] = JSON.parse(
                          field.value || "[]"
                        );
                        return parsedFiles.map((x) => ({
                          file_name: x.ten_file,
                          url: x.duong_dan,
                        }));
                      } catch (error) {
                        console.error("Lỗi khi parse attach_files:", error);
                        return [];
                      }
                    })()}
                    onValueChanged={(files) => {
                      field.onChange(
                        JSON.stringify(
                          files.map((x) => ({
                            duong_dan: x.url,
                            ten_file: x.file_name,
                            id: 0,
                          }))
                        )
                      );
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>

          <ModalActions>
            <Button
              text="Đóng"
              onClick={handleClose}
            />
            <Button
              text={isSaving ? "Đang lưu..." : "Lưu và tiếp tục"}
              variant="primary"
              type="submit"
            />
          </ModalActions>
        </form>
      )}

      {currentStep === 2 && canGoToStep2 && (
        <Box>
          <MienGiamItemsModal
            sf_hinhthucmiengiam={currentRecord}
            animationOf="#btnEditItems"
            onClose={() => undefined}
            onEditRequest={() => setCurrentStep(1)}
            onRegisterSubmit={(submitFn) => {
              setSubmitStep2Changes(() => submitFn);
            }}
            onSavingChange={setIsSavingStep2}
          />
          <ModalActions>
            <Button
              text="Quay lại bước 1"
              onClick={() => setCurrentStep(1)}
            />
            <Button
              text={isSavingStep2 ? "Đang lưu..." : "Hoàn tất"}
              variant="primary"
              onClick={handleFinishStep2}
              disabled={isSavingStep2}
            />
          </ModalActions>
        </Box>
      )}
    </Modal>
  );
};

export default HinhThucMienGiamWizardModal;
